/* eslint-disable no-undef */
import {
    Button, Card, Col, Row, Select, Skeleton, Space, Typography,
} from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { IAvitoChatLayout } from '../../types/layouts/avito-chat';
import { useApiFetch } from '../../hooks/api/useApiFetch';
import AvitoAttachPhoneModal from './attach-phone-modal';
import AvitoChatNoteForm from './note-form';
import { useModalEdit } from '../../hooks/table/useModalEdit';
import AvitoChatMessages from './messages';
import LayoutFactory from '../../factories/layout';

export interface IAvitoChatInfo {
    id: number;
    client: {
        name: string;
        phone: string | null;
        url: string;
    };
    item_title: string;
    item_url: string;
    last_message_created_at: string;
    last_message_direction: 'in' | 'out';
    last_message_text: string;
    note: string;
    chats_count: number;
    order_source: {
        id: number;
        name: string;
        color?: string;
        text_color?: string;
    };
    read: boolean;
    sender: {
        id: number;
        fio: string;
    } | null;
    tags: {
        id: number;
        name: string;
        text_color: string | null;
        color: string | null;
    }[];
    created_at: string;
    orders: number[];
}

const AvitoChat = ({ params }: { params: IAvitoChatLayout }) => {
    const api = useApiFetch();
    const [info, setInfo] = useState<IAvitoChatInfo>();
    const [loading, setLoading] = useState(false);
    const [showOrderForm, setShowOrderForm] = useState(false);
    const tagsTimeout = useRef<NodeJS.Timeout>();
    const infoLoaded = useRef(false);
    const modal = useModalEdit();

    const getInfo = async () => {
        setLoading(true);
        const response = await api.fetchData<IAvitoChatInfo>(`/avito-chats/${params.id}`, 'get', true);
        if (response.data) {
            setInfo(response.data);
        }
        setLoading(false);
        infoLoaded.current = true;
    };

    useEffect(() => {
        getInfo();
    }, []);

    useEffect(() => {
        if (!modal.visible) {
            setShowOrderForm(false);
            if (infoLoaded.current) {
                getInfo();
            }
        }
    }, [modal.visible]);

    const onTagsChange = (tags: number[]) => {
        api.cancelRequest();
        // @ts-ignore
        clearTimeout(tagsTimeout.current);
        tagsTimeout.current = setTimeout(() => {
            api.fetchData(`/avito-chats/${params.id}/tags`, 'post', true, { tags });
        }, 1000);
    };

    const onOrderCreateClick = () => {
        if (params.order_access) {
            modal.setObjectId(undefined);
            setShowOrderForm(true);
            modal.getLayout({
                layoutLink: `/orders/create/layout?avito_chat_id=${params.id}`,
            });
            modal.show();
        }
    };

    const onOrderLinkClick = (orderId: number) => {
        if (params.order_access) {
            setShowOrderForm(true);
            modal.getLayout({
                layoutLink: `/orders/update/layout?id=${orderId}`,
            });
            modal.show(orderId);
        }
    };

    const onOrderFromClose = () => {
        modal.hide();
    };

    const extra = (
        <Space direction="vertical" style={{ flex: 1, width: '100%' }} size="large">
            <Space
                align="center"
                size="large"
                style={{
                    flex: 1,
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Space size="large">
                    <Typography.Title level={5}>Чат с клиентом</Typography.Title>
                    <Space>
                        <Select
                            defaultValue={params.tags.selected}
                            placeholder="Тег"
                            mode="multiple"
                            options={params.tags.options}
                            style={{ minWidth: 120 }}
                            onChange={onTagsChange}
                            loading={loading}
                        />
                        <AvitoAttachPhoneModal
                            id={params.id}
                            phone={info?.client.phone}
                            onUpdated={getInfo}
                        />
                    </Space>
                </Space>
                <Space>
                    <Button
                        hidden={!params.order_access}
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onOrderCreateClick}
                    />
                </Space>
            </Space>
            {info && (
                <Skeleton loading={loading}>
                    <Row>
                        <Col xs={8}>
                            <Space direction="vertical">
                                <span>
                                    Аккаунт:
                                    {' '}
                                    <span style={{
                                        backgroundColor: info?.order_source.color, color: info?.order_source.text_color, padding: 5, paddingTop: 2, paddingBottom: 2,
                                    }}
                                    >
                                        {info?.order_source.name}
                                    </span>
                                    {' '}
                                    <a href={info.item_url} target="_blank" rel="noreferrer">{info.item_title}</a>
                                </span>
                                <span>
                                    Беседа начата:
                                    {' '}
                                    {info.created_at}
                                </span>
                                <span>
                                    Клиент:
                                    {' '}
                                    <a href={info.client.url} target="_blank" rel="noreferrer">{info.client.name}</a>
                                </span>
                                <span>
                                    Всего чатов:
                                    {' '}
                                    {info.chats_count}
                                </span>
                            </Space>
                        </Col>
                        <Col xs={8}>
                            <Space direction="vertical">
                                <span>
                                    Добавленные теги:
                                    {' '}
                                    <Space direction="horizontal">
                                        {info.tags.map((tag: any) => (
                                            <span key={tag.id} style={{ backgroundColor: tag.color, color: tag.text_color, padding: 2 }}>{tag.name}</span>
                                        ))}
                                    </Space>
                                </span>
                                <span>
                                    Добавленный телефон:
                                    {' '}
                                    {info.client.phone || '-'}
                                </span>
                                <span>
                                    Номер заказа:
                                    {' '}
                                    {info.orders.map((orderId) => (
                                        <Button key={orderId} type="link" size="small" onClick={() => onOrderLinkClick(orderId)}>{orderId}</Button>
                                    ))}
                                </span>
                                <span>
                                    Номер чата:
                                    {' '}
                                    {info.id}
                                </span>
                            </Space>
                        </Col>
                        <Col xs={8}>
                            <AvitoChatNoteForm id={params.id} note={info.note} />
                        </Col>
                    </Row>
                </Skeleton>
            )}
        </Space>
    );

    const formExtra = (
        <Button icon={<CloseOutlined />} onClick={onOrderFromClose} danger />
    );

    return (
        <div className="avito-chat-page">
            <Card className="w-100" extra={extra}>
                {info && (
                    <AvitoChatMessages id={params.id} channel={params.channel} chat={info} sendDisabled={showOrderForm} />
                )}
            </Card>
            <Card className="avito-chat-order-card" extra={formExtra} hidden={!showOrderForm}>
                <div className="avito-chat-order-card-content">
                    <Skeleton loading={modal.layoutLoading}>
                        <LayoutFactory layout={modal.layout} modalEdit={modal} />
                    </Skeleton>
                </div>
            </Card>
        </div>
    );
};

export default AvitoChat;
